/**
 * src/player/queue.ts — a decisão "O QUE TOCAR", com o DOMÍNIO do gestor.
 *
 * Este arquivo é a prova de que o receptor não tem um segundo motor de rotação.
 * Tudo que exige regra de produto (ordem do loop, duração, quem está ativo AGORA)
 * vem de `src/domain/*` — cópia verbatim de `SimpleIndor/src/domain`
 * (`npm run sync:domain`; o build QUEBRA se divergir).
 *
 * Por que um arquivo só para isto: o resto do player precisa responder "o que faço
 * com o próximo item", e essa pergunta tem três respostas que vêm de regras
 * diferentes (ordem do backend, duração do domínio, janela do relógio local). Juntar
 * as três aqui deixa o motor de exibição puramente mecânico — e é o motor de
 * exibição que o dev-pleno não pode quebrar quando ligar a rede.
 */
import type { Ad, ID, PlaybackItem } from '../domain/types';
import {
  buildPlaybackQueue,
  queueLoopSeconds,
  resolveDurationSeconds,
} from '../domain/rotation';
import { MIN_ITEM_SECONDS } from '../domain/rotation';
import { isAdActiveAt } from '../domain/schedule';
import { computeQueueVersion, isQueueVersionValid } from '../domain/queueSignature';
import type { QueuePayload } from '../api/types';

/** `items` sempre é lista no contrato; dado sujo de cache é caso de corrupt-cache, não de produto. */
function itemsOf(payload: Pick<QueuePayload, 'items'> | undefined): PlaybackItem[] {
  return Array.isArray(payload?.items) ? payload.items.filter(Boolean) : [];
}

/**
 * Item PRONTO PARA EXIBIR: o `PlaybackItem` do backend + a duração que o motor vai
 * respeitar. A duração DIVERGE de `item.durationSeconds` quando o item é de VÍDEO:
 * quem manda é o fim real do arquivo, não o metadado (PLAYER §4.2).
 */
export interface DisplayItem {
  adId: ID;
  media: PlaybackItem['media'];
  /**
   * Duração DECLARADA do item, em segundos (o que veio do backend, já resolvido pelo
   * domínio). Para vídeo, é a metainformação do arquivo: quem manda mesmo é o evento
   * `ended`, e é por isso que `advancesOn` é `media-end`.
   */
  holdSeconds: number;
  kind: 'image' | 'video';
  /** `media-end` = o motor avança no evento de término do vídeo, nunca por relógio. */
  advancesOn: 'timer' | 'media-end';
  /**
   * Modo de escala do anúncio — vem do backend (escolha do gestor) e precisa
   * ser honrado pelo player (PLAYER §4.5). `cover` preenche a tela cortando
   * o que sobrar; `contain` mostra inteiro com barras; `stretch` distorce
   * para preencher. A ausência significa `contain` (o que o player já fazia
   * antes deste campo existir — não é default "novo").
   */
  scaleMode?: 'contain' | 'cover' | 'stretch';
  /**
   * Rotação em graus (0/90/180/270). A TV na vertical gira 90 e o anúncio
   * vira com ela; sem este campo, o item sempre sai 0 (o default legado).
   */
  rotation?: 0 | 90 | 180 | 270;
}

/**
 * `PlaybackItem` → `DisplayItem`, aplicando a regra de vídeo do PLAYER §4.2.
 *
 * IMAGEM: `holdSeconds = durationSeconds` (10 s por padrão) — o motor espera o relógio.
 * VÍDEO: `holdSeconds` é metadado, `advancesOn = 'media-end'`. O motor NÃO espera
 * `durationSeconds` POR CIMA de um vídeo que já acabou: isso criaria uma pausa preta
 * entre anúncios, que é o defeito que o §4.2 existe para proibir.
 */
export function toDisplayItem(item: PlaybackItem): DisplayItem {
  const kind = item.media?.kind === 'video' ? 'video' : 'image';
  const declared = Number.isFinite(item.durationSeconds) ? item.durationSeconds : 0;
  // Vídeo NÃO tem espera de relógio: `advancesOn='media-end'` diz que quem manda é o
  // evento de término. Mas `holdSeconds` continua sendo a duração real declarada —
  // é dela que sai o orçamento do watchdog. `MIN_ITEM_SECONDS` evita metadado 0/NaN.
  const holdSeconds = Math.max(MIN_ITEM_SECONDS, Math.round(declared));
  return {
    adId: item.adId,
    media: item.media,
    holdSeconds,
    kind,
    advancesOn: kind === 'video' ? 'media-end' : 'timer',
    // `scaleMode`/`rotation` viajam no `PlaybackItem` desde 2026-09; a omissão
    // aqui era o motivo de a TV web ignorar a escolha do gestor (BUG-2026-10-14).
    // A checagem é `!== undefined` (e não truthy) porque `rotation: 0` é
    // legítimo e não pode virar `undefined` ao propagar.
    ...(item.scaleMode !== undefined ? { scaleMode: item.scaleMode } : {}),
    ...(item.rotation !== undefined ? { rotation: item.rotation } : {}),
  };
}

/**
 * Fila em memória, já filtrada para o AGORA.
 *
 * ⚠️ REVALIDAÇÃO OFFLINE (o requisito mais importante do PLAYER §4.4 = critério 7 da
 * §9 do contrato-base): o payload carrega `ads[]` com o agendamento CRU justamente
 * para isto. Um anúncio "até 22:00" precisa SAIR às 22:00 mesmo com o Wi-Fi
 * desligado — confiando só em `items[]`, ele ficaria na TV até o servidor responder.
 * Por isso cada item passa por `isAdActiveAt` com o relógio do próprio receptor
 * (mesmo código que o backend usou para gerar a fila).
 *
 * Um item cujo anúncio expirou/foi pausado SAI DO LOOP na próxima volta, sem cortar
 * o que está no ar agora: o item atual termina o tempo dele normalmente e a próxima
 * volta já não o contém. É a diferença entre "o anúncio sai no horário certo" (exigido)
 * e "o anúncio some no meio" (proibido pelo §4.3).
 *
 * O motor revalida cada candidato antes de exibir. A casca precisa reconstruir a
 * fila pelo relógio corrigido para ativar janelas futuras e retirar o item no ar
 * ao vencer; apenas receber heartbeat não revalida a programação.
 */
export class ActiveQueue {
  readonly screenId: ID;
  /** `version` (ETag) desta fila. `undefined` enquanto não houve nenhum 200 válido. */
  readonly version: string | undefined;
  readonly receivedAt: Date;
  readonly loopSeconds: number;
  private readonly all: DisplayItem[];
  private readonly adsById: Map<ID, Ad>;

  /** Recheck candidates at playback boundaries using the corrected receiver clock. */
  isActive(item: DisplayItem, now: Date): boolean {
    const ad = this.adsById.get(item.adId);
    return ad === undefined || isAdActiveAt(ad, now);
  }

  constructor(payload: QueuePayload | undefined, now: Date) {
    this.screenId = payload?.screenId ?? '';
    this.version = typeof payload?.version === 'string' ? payload.version : undefined;
    this.receivedAt = payload?.generatedAt ? new Date(payload.generatedAt) : now;

    const ads = Array.isArray(payload?.ads) ? payload.ads.filter(Boolean) : [];
    const adsById = new Map<ID, Ad>(ads.map(ad => [ad.id, ad]));
    this.adsById = adsById;

    const displayable = itemsOf(payload).filter(item => {
      const ad = adsById.get(item.adId);
      // Item SEM registro em `ads[]` (fila parcial/cache antigo): não há o que
      // revalidar, executa. Item COM registro que expirou: sai do loop.
      return ad === undefined || isAdActiveAt(ad, now);
    });

    this.all = displayable.map(toDisplayItem);
    this.loopSeconds = this.all.reduce((sum, item) => sum + item.holdSeconds, 0);
  }

  get size(): number {
    return this.all.length;
  }

  /** `true` quando não há nada exibível agora (fila vazia, tudo expirado ou pausado). */
  get isEmpty(): boolean {
    return this.all.length === 0;
  }

  /** Snapshot dos itens ativos AGORA — o que a §9 do PLAYER exige do receptor. */
  items(): DisplayItem[] {
    return this.all.slice();
  }
}

/**
 * Reconstrói a fila como o backend a construiria, no instante `now`.
 *
 * POR QUE ISTO EXISTE: com D9, quem avalia `weekly` é a TV (o backend manda a faixa
 * CRUA porque roda em outro fuso). Passar esses `ads` pelo MESMO `buildPlaybackQueue`
 * do backend, com o relógio da TV, dá a fila que o backend teria devolvido se
 * estivesse no mesmo fuso — mesma ordem, mesma fase, mesma `version`. É o que faz
 * "offline" e "online" mostrarem a mesma coisa em vez de divergirem em silêncio.
 *
 * `rotationEpochSeconds` do payload é usado quando vem (D1: a origem do loop é do
 * servidor); sem ele, o `ROTATION_EPOCH_SECONDS` fixo do domínio, que é o mesmo
 * número — o campo existe para o backend poder mudar a origem sem release do receptor.
 */
export function rebuildQueueForScreen(
  screenId: ID,
  ads: Ad[],
  now: Date,
  rotationEpochSeconds?: number,
): { items: PlaybackItem[]; version: string; loopSeconds: number } {
  const queue = buildPlaybackQueue(
    screenId,
    ads,
    now,
    rotationEpochSeconds === undefined ? undefined : { rotationEpochSeconds },
  );
  return {
    items: queue.items,
    version: queue.version,
    loopSeconds: queueLoopSeconds(queue),
  };
}

/**
 * Confia na fila em cache? `isQueueVersionValid` recalcula o hash do conteúdo e
 * compara. Fila corrompida (queda de energia no meio da escrita) reprova aqui e vira
 * tela de espera — nunca JSON malformado virando crash loop (PLAYER §4.4).
 */
export function isCacheUsable(payload: QueuePayload | undefined | null): boolean {
  if (!payload || typeof payload.version !== 'string' || !Array.isArray(payload.items)) {
    return false;
  }
  if (payload.items.some(item => !item || typeof item.adId !== 'string' ||
    !item.media || typeof item.media.id !== 'string' ||
    !['image', 'video'].includes(item.media.kind) || !Number.isFinite(item.durationSeconds))) {
    return false;
  }
  return isQueueVersionValid({
    screenId: payload.screenId,
    version: payload.version,
    items: payload.items,
    generatedAt: payload.generatedAt,
  });
}

/** Reexportado para a UI/log: a MESMA função de hash do backend, nunca uma cópia. */
export { computeQueueVersion, isQueueVersionValid, queueLoopSeconds, resolveDurationSeconds };
